<template>
  <div class="pwa-signup-page">
    <div class="signup-container">
      <!-- 헤더 -->
      <div class="signup-header">
        <button class="back-button" @click="goToLogin" :disabled="loading">
          <v-icon>mdi-arrow-left</v-icon>
        </button>
        <h1 class="signup-title">회원가입</h1>
        <div class="header-spacer"></div>
      </div>

      <!-- 회원가입 폼 -->
      <div class="form-content">
        <v-form ref="signupForm" @submit.prevent="handleSignup">
          <!-- 아이디 -->
          <div class="form-field">
            <label class="field-label">아이디 *</label>
            <div class="input-with-button">
              <input
                v-model="signupData.m_id"
                type="text"
                class="form-input"
                placeholder="6자 이상 입력하세요"
                :disabled="loading || checkingId"
                maxlength="20"
                autocomplete="username"
                @input="onIdInput"
              />
              <button
                type="button"
                class="check-button"
                :disabled="!signupData.m_id || signupData.m_id.length < 6 || loading || checkingId"
                @click="checkIdDuplicate"
              >
                <v-progress-circular
                  v-if="checkingId"
                  indeterminate
                  size="16"
                  width="2"
                  color="white"
                ></v-progress-circular>
                <span v-else>중복확인</span>
              </button>
            </div>
            <div v-if="idCheckMessage" :class="['message', idCheckStatus]">
              {{ idCheckMessage }}
            </div>
            <div v-if="!idCheckMessage && signupData.m_id && signupData.m_id.length < 6" class="message error">
              6자 이상 입력해주세요.
            </div>
          </div>

          <!-- 장기요양기관기호 -->
          <div class="form-field">
            <label class="field-label">장기요양기관기호 *</label>
            <input
              v-model="signupData.m_longterm_code"
              type="text"
              class="form-input"
              placeholder="장기요양기관기호를 입력하세요"
              :disabled="loading"
              autocomplete="off"
              maxlength="15"
            />
          </div>

          <!-- 비밀번호 -->
          <div class="form-field">
            <label class="field-label">비밀번호 *</label>
            <div class="input-with-icon">
              <input
                v-model="signupData.m_pw"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="비밀번호를 입력하세요"
                :disabled="loading"
                autocomplete="new-password"
                maxlength="50"
              />
              <button
                type="button"
                class="icon-button"
                @click="showPassword = !showPassword"
              >
                <v-icon size="20">{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
              </button>
            </div>
          </div>

          <!-- 비밀번호 확인 -->
          <div class="form-field">
            <label class="field-label">비밀번호 확인 *</label>
            <div class="input-with-icon">
              <input
                v-model="signupData.confirmPw"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="비밀번호를 다시 입력하세요"
                :disabled="loading"
                autocomplete="new-password"
                maxlength="50"
              />
              <button
                type="button"
                class="icon-button"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <v-icon size="20">{{ showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
              </button>
            </div>
            <div v-if="signupData.confirmPw && signupData.m_pw && signupData.confirmPw !== signupData.m_pw" class="message error">
              비밀번호가 일치하지 않습니다.
            </div>
            <div v-if="signupData.confirmPw && signupData.m_pw && signupData.confirmPw === signupData.m_pw" class="message success">
              비밀번호가 일치합니다.
            </div>
          </div>

          <!-- 약관 동의 -->
          <div class="terms-section">
            <label class="terms-label">
              <input
                v-model="agreeTerms"
                type="checkbox"
                class="terms-checkbox"
                :disabled="loading"
              />
              <span class="terms-text">
                <a href="#" @click.prevent="showTermsModal = true" class="terms-link">이용약관</a> 및 
                <a href="#" @click.prevent="showPrivacyModal = true" class="terms-link">개인정보처리방침</a>에 동의합니다.
              </span>
            </label>
          </div>

          <!-- 회원가입 버튼 -->
          <button
            type="button"
            class="submit-button"
            :disabled="!isFormValid || isSubmitting"
            :class="{ 'loading': loading }"
            @click="handleSignup"
          >
            <span v-if="!loading">회원가입</span>
            <span v-else class="loading-text">처리 중...</span>
          </button>
        </v-form>
      </div>
    </div>

    <!-- 이용약관 모달 -->
    <v-dialog v-model="showTermsModal" max-width="90%" scrollable>
      <v-card rounded="lg">
        <v-card-title class="modal-title">이용약관</v-card-title>
        <v-card-text class="modal-content">
          <div class="terms-content">
            <h3>제1조 (목적)</h3>
            <p>이 약관은 재가장기요양센터 관리 시스템(이하 "서비스")을 제공하는 하이파이브 주식회사(이하 "회사")와 이를 이용하는 회원(이하 "이용자") 간의 서비스 이용에 관한 제반 사항과 기타 필요한 사항을 구체적으로 규정함을 목적으로 합니다.</p>

            <h3>제2조 (정의)</h3>
            <p>1. "서비스"라 함은 재가장기요양센터의 운영 관리를 위한 웹 기반 시스템을 의미합니다.</p>
            <p>2. "이용자"라 함은 이 약관에 따라 회사가 제공하는 서비스를 받는 회원을 말합니다.</p>
            <p>3. "회원"이라 함은 회사에 개인정보를 제공하여 회원등록을 한 자로서, 회사의 정보를 지속적으로 제공받으며, 회사가 제공하는 서비스를 계속적으로 이용할 수 있는 자를 말합니다.</p>

            <h3>제3조 (약관의 효력 및 변경)</h3>
            <p>1. 이 약관은 서비스를 신청하여 회사의 승낙을 받은 때부터 효력이 발생합니다.</p>
            <p>2. 회사는 약관을 변경하는 경우 변경사유 및 적용일자를 명시하여 현행약관과 함께 서비스 초기화면에 그 적용일자 7일 이전부터 적용일자 전일까지 공지합니다.</p>
            <p>3. 회원이 변경된 약관에 동의하지 않는 경우, 회원은 서비스 이용계약을 해지할 수 있습니다.</p>
          </div>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn variant="text" block @click="showTermsModal = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 개인정보처리방침 모달 -->
    <v-dialog v-model="showPrivacyModal" max-width="90%" scrollable>
      <v-card rounded="lg">
        <v-card-title class="modal-title">개인정보처리방침</v-card-title>
        <v-card-text class="modal-content">
          <div class="privacy-content">
            <h3>1. 개인정보의 처리목적</h3>
            <p>하이파이브 주식회사(이하 "회사")는 다음의 목적을 위하여 개인정보를 처리하고 있으며, 다음의 목적 이외의 용도로는 이용하지 않습니다.</p>
            <p>- 회원 가입 및 관리</p>
            <p>- 재가장기요양센터 운영 관리 서비스 제공</p>
            <p>- 고객 상담 및 불만 처리</p>
            <p>- 서비스 개선 및 신규 서비스 개발</p>

            <h3>2. 개인정보의 처리 및 보유기간</h3>
            <p>① 회사는 정보주체로부터 개인정보를 수집할 때 동의받은 개인정보 보유·이용기간 또는 법령에 따른 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
            <p>② 구체적인 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
            <p>- 회원 가입 및 관리: 회원 탈퇴 시까지</p>
            <p>- 서비스 제공: 서비스 이용계약 종료 시까지</p>
            <p>- 고객 상담: 상담 완료 후 3년</p>
          </div>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn variant="text" block @click="showPrivacyModal = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="2500"
      rounded="lg"
      location="top"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

interface SignupData {
  m_id: string;
  m_pw: string;
  confirmPw: string;
  m_longterm_code: string;
  m_role: string;
  m_status: string;
  m_isuse: string;
}

const router = useRouter();

const signupData = ref<SignupData>({
  m_id: '',
  m_pw: '',
  confirmPw: '',
  m_longterm_code: '',
  m_role: 'user',
  m_status: 'req',
  m_isuse: 'N',
});

const agreeTerms = ref(false);
const loading = ref(false);
const isSubmitting = ref(false);
const signupForm = ref();
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const snackbar = ref({ show: false, message: '', color: 'info' });
const showTermsModal = ref(false);
const showPrivacyModal = ref(false);
const checkingId = ref(false);
const idCheckMessage = ref('');
const idCheckStatus = ref('');
const isIdAvailable = ref(false);

const isFormValid = computed(() => {
  const d = signupData.value;
  // 아이디 중복 확인, 비밀번호 일치, 약관 동의만 확인
  return isIdAvailable.value && 
         d.m_pw && 
         d.confirmPw && 
         d.m_pw === d.confirmPw && 
         agreeTerms.value;
});

const handleSignup = async () => {
  if (!isFormValid.value) {
    if (!isIdAvailable.value) {
      snackbar.value.message = '아이디 중복확인이 필요합니다.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    if (!signupData.value.m_pw || !signupData.value.confirmPw) {
      snackbar.value.message = '비밀번호를 입력해주세요.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    if (signupData.value.m_pw !== signupData.value.confirmPw) {
      snackbar.value.message = '비밀번호가 일치하지 않습니다.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    if (!agreeTerms.value) {
      snackbar.value.message = '이용약관에 동의해주세요.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
      return;
    }
    return;
  }
  if (isSubmitting.value) return;
  
  isSubmitting.value = true;
  loading.value = true;
  
  try {
    const { confirmPw, ...signupFormData } = signupData.value;
    
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ form: signupFormData }),
    });
    
    const result = await response.json();
    
    if (result.success) {
      snackbar.value.message = '회원가입이 완료되었습니다.';
      snackbar.value.color = 'success';
      snackbar.value.show = true;
      setTimeout(() => router.push('/pwa/login'), 800);
    } else {
      snackbar.value.message = result.message || '회원가입에 실패했습니다.';
      snackbar.value.color = 'error';
      snackbar.value.show = true;
    }
  } catch (error) {
    console.error('회원가입 오류:', error);
    snackbar.value.message = '회원가입 중 오류가 발생했습니다.';
    snackbar.value.color = 'error';
    snackbar.value.show = true;
  } finally {
    loading.value = false;
    isSubmitting.value = false;
  }
};

const goToLogin = () => {
  router.push('/pwa/login');
};

const onIdInput = () => {
  idCheckMessage.value = '';
  idCheckStatus.value = '';
  isIdAvailable.value = false;
};

const checkIdDuplicate = async () => {
  if (!signupData.value.m_id || signupData.value.m_id.length < 6) {
    idCheckMessage.value = '6자 이상 입력해주세요.';
    idCheckStatus.value = 'error';
    return;
  }

  checkingId.value = true;
  idCheckMessage.value = '';
  idCheckStatus.value = '';

  try {
    const response = await fetch(`/api/auth/memberIdCheck/${signupData.value.m_id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (result.success) {
      idCheckMessage.value = '이미 사용 중인 아이디입니다.';
      idCheckStatus.value = 'error';
      isIdAvailable.value = false;
    } else {
      idCheckMessage.value = '사용 가능한 아이디입니다.';
      idCheckStatus.value = 'success';
      isIdAvailable.value = true;
    }
  } catch (error) {
    console.error('아이디 중복확인 오류:', error);
    idCheckMessage.value = '중복확인 중 오류가 발생했습니다.';
    idCheckStatus.value = 'error';
    isIdAvailable.value = false;
  } finally {
    checkingId.value = false;
  }
};
</script>

<style scoped lang="scss">
.pwa-signup-page {
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
}

.signup-container {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.signup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.back-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #4b5563;

  &:hover:not(:disabled) {
    background: #e5e7eb;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.signup-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  flex: 1;
  text-align: center;
  letter-spacing: -0.5px;
}

.header-spacer {
  width: 40px;
}

.form-content {
  width: 100%;
}

.form-field {
  margin-bottom: 24px;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-family: 'Noto Sans KR', sans-serif;
}

.input-with-button {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.input-with-icon {
  position: relative;
  width: 100%;
}

.form-input {
  width: 100%;
  height: 52px;
  padding: 0 16px;
  font-size: 15px;
  color: #1a1a1a;
  background-color: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  font-family: 'Noto Sans KR', sans-serif;

  &:focus {
    outline: none;
    border-color: #667eea;
    background-color: #ffffff;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.check-button {
  padding: 0 20px;
  height: 52px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-family: 'Noto Sans KR', sans-serif;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
}

.icon-button {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
}

.message {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  font-family: 'Noto Sans KR', sans-serif;

  &.success {
    color: #059669;
    background-color: #d1fae5;
  }

  &.error {
    color: #dc2626;
    background-color: #fee2e2;
  }
}

.terms-section {
  margin: 32px 0 24px 0;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 12px;
}

.terms-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  user-select: none;
}

.terms-checkbox {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #667eea;
  flex-shrink: 0;
}

.terms-text {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
  font-family: 'Noto Sans KR', sans-serif;
}

.terms-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

.submit-button {
  width: 100%;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Noto Sans KR', sans-serif;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.loading {
    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  }
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 24px;
}

.terms-content,
.privacy-content {
  line-height: 1.8;
  font-family: 'Noto Sans KR', sans-serif;

  h3 {
    color: #667eea;
    font-size: 16px;
    font-weight: 700;
    margin-top: 24px;
    margin-bottom: 12px;

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    margin-bottom: 12px;
    color: #374151;
    font-size: 14px;
  }
}

.modal-actions {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
}

@media (max-width: 480px) {
  .signup-container {
    padding: 20px;
    border-radius: 20px;
  }

  .signup-title {
    font-size: 20px;
  }

  .form-input,
  .check-button,
  .submit-button {
    height: 48px;
    font-size: 14px;
  }
}
</style>

